/**
 * Zwischenspeicher für signierte Adressen (0429, privater Bucket ig-media). Listen und Ansichten fragen viele Bilder auf einmal an:
 * Bekannte Adressen, die noch mindestens fünf Minuten gelten, kommen aus dem Speicher; nur die übrigen gehen gebündelt (höchstens
 * 100 je Anfrage) an die Signierfunktion. Pfade ohne Adresse (keine Rechte, Datei fehlt) fehlen im Ergebnis.
 */
export class SignedUrlCache {
  private entries = new Map<string, { url: string; until: number }>();

  constructor(
    private readonly sign: (paths: string[]) => Promise<Record<string, string>>,
    private readonly ttlMs = 3_600_000,
    private readonly now: () => number = () => Date.now(),
    private readonly reserveMs = 300_000,
  ) {}

  /** Nach einem Wechsel der angemeldeten Person: nichts der vorigen Person weiterverwenden */
  clear(): void {
    this.entries.clear();
  }

  async get(paths: readonly string[]): Promise<Record<string, string>> {
    const want = [...new Set(paths.filter((p) => typeof p === "string" && p.length > 0))];
    const start = this.now();
    const fresh = (p: string) => {
      const e = this.entries.get(p);
      return !!e && e.until - start > this.reserveMs;
    };
    const missing = want.filter((p) => !fresh(p));
    for (let i = 0; i < missing.length; i += 100) {
      const chunk = missing.slice(i, i + 100);
      const signed = await this.sign(chunk);
      const until = start + this.ttlMs;
      for (const p of chunk) {
        const url = signed[p];
        if (typeof url === "string" && url) this.entries.set(p, { url, until });
      }
    }
    const out: Record<string, string> = {};
    for (const p of want) {
      const e = this.entries.get(p);
      if (e && e.until > this.now()) out[p] = e.url;
    }
    return out;
  }
}

/**
 * Signierte Adressen der Serverfunktion ig-share auf die Adresse des Speichers dieser App umschreiben (0429). Die Serverfunktion
 * signiert mit ihrer eigenen Projektadresse; die Signatur gilt für Bucket und Pfad, nicht für den Host. So kommen die Bilder immer
 * von VITE_SUPABASE_URL, die die Content-Security-Policy (img-src) erlaubt. Ungültige Einträge fallen weg.
 */
export function aufSpeicherBasis(urls: unknown, base: string, bucket = "ig-media"): Record<string, string> {
  const out: Record<string, string> = {};
  if (!urls || typeof urls !== "object" || Array.isArray(urls)) return out;
  const root = base.replace(/\/+$/, "");
  for (const [path, raw] of Object.entries(urls as Record<string, unknown>)) {
    if (typeof raw !== "string" || !/^[0-9a-f-]{36}\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(jpg|png|webp)$/.test(path)) continue;
    try {
      const u = new URL(raw);
      const token = u.searchParams.get("token");
      if (!token || u.pathname !== `/storage/v1/object/sign/${bucket}/${path}`) continue;
      out[path] = `${root}/storage/v1/object/sign/${bucket}/${path}?token=${encodeURIComponent(token)}`;
    } catch {
      /* ungültige Adresse: kein Bild */
    }
  }
  return out;
}
